import { useEffect, useState } from 'react';
import { CATEGORY_COLORS, categoryColor, type SkillDef } from '@/mocks/pipeline';
import { getSkill } from '@/api';
import MarkdownPreview from './MarkdownPreview';

type Props = {
  skill: SkillDef;
  onClose: () => void;
};

export default function SkillPreviewDrawer({ skill, onClose }: Props) {
  const color = CATEGORY_COLORS[skill.category] || categoryColor(skill.category);
  const [body, setBody] = useState(skill.body || '');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (skill.body) {
      setBody(skill.body);
      return;
    }
    let cancelled = false;
    getSkill(skill.id)
      .then((s) => {
        if (!cancelled) setBody(s.body || '');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [skill.id, skill.body]);

  return (
    <aside
      className="w-80 shrink-0 border-r border-border bg-bg flex flex-col overflow-hidden animate-slide-in"
      aria-label={`${skill.name} preview`}
    >
      <div className="shrink-0 p-4 border-b border-border">
        <div className="flex items-start gap-2.5">
          <div
            className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
            style={{ background: `${color}22`, color }}
          >
            <i className={`${skill.icon || 'ri-cpu-line'} text-base`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-textp truncate">{skill.name}</h2>
              {skill.version ? (
                <span className="shrink-0 text-[11px] font-mono px-1.5 py-0.5 rounded bg-surface border border-border text-texts">
                  {skill.version}
                </span>
              ) : null}
            </div>
            <div className="text-[11px] text-texts mt-0.5">{skill.category}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="shrink-0 w-7 h-7 rounded-md text-texts hover:text-textp hover:bg-surface flex items-center justify-center"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>
        {skill.description ? (
          <p className="text-xs text-texts mt-3 leading-relaxed">{skill.description}</p>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <Schema title="Input schema" items={skill.inputs ?? []} />
        <Schema title="Output schema" items={skill.outputs ?? []} />
        {skill.latency ? <div className="text-[11px] text-texts">{skill.latency}</div> : null}

        <div>
          <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">Skill body</div>
          {body ? (
            <MarkdownPreview content={body} compact />
          ) : (
            <div className="text-xs text-texts bg-surface/60 border border-border rounded-md p-3">
              No prompt body for this skill.
            </div>
          )}
        </div>
      </div>

      <div className="shrink-0 px-4 py-2.5 border-t border-border text-[11px] text-texts flex items-center gap-1.5">
        <i className="ri-information-line" />
        Drag onto the canvas to add this skill
      </div>
    </aside>
  );
}

function Schema({ title, items }: { title: string; items: { name: string; type: string }[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">{title}</div>
      <div className="space-y-1">
        {items.map((it) => (
          <div key={it.name} className="flex items-center justify-between bg-surface/60 border border-border rounded-md px-2.5 py-1.5">
            <span className="font-mono text-[12px] text-textp">{it.name}</span>
            <span className="font-mono text-[11px] text-texts">{it.type}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
